import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileUp, Mail, MessageCircle, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import AppShell from "../components/AppShell";
import DatePickerField from "../components/DatePickerField";
import { api } from "../lib/api";

const emptySupplier = {
  name: "",
  supplier_type: "Persona moral",
  rfc: "",
  tax_regime: "",
  category: "",
  email: "",
  phone: "",
  whatsapp: "",
  address: "",
  status: "Activo",
};

const money = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});

function supplierInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase() || "PR";
}

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [form, setForm] = useState(emptySupplier);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteReminder, setNoteReminder] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [docCategory, setDocCategory] = useState("Otro");
  const [docFilter, setDocFilter] = useState("Todas");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [editingNoteDate, setEditingNoteDate] = useState("");
  const uploadInput = useRef(null);

  const refresh = useCallback(async (search = "") => {
    try {
      const response = await api.get("/workspace/suppliers", { params: { q: search }, withCredentials: true });
      setSuppliers(response.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar tus proveedores.");
    }
  }, []);

  const loadSupplierDetails = async (supplier) => {
    setSelectedSupplier(supplier);
    setForm({ ...emptySupplier, ...supplier });
    setIsEditing(false);
    setIsCreating(false);
    setNotice("");
    setError("");
    try {
      const [documentsResponse, invoicesResponse, notesResponse] = await Promise.all([
        api.get(`/suppliers/${supplier.id}/documents`, { withCredentials: true }),
        api.get(`/suppliers/${supplier.id}/invoices`, { withCredentials: true }),
        api.get(`/suppliers/${supplier.id}/notes`, { withCredentials: true }),
      ]);
      setDocuments(documentsResponse.data);
      setInvoices(invoicesResponse.data);
      setNotes(notesResponse.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar el detalle del proveedor.");
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => refresh(query), 250);
    return () => window.clearTimeout(timeout);
  }, [query, refresh]);

  const activeCount = useMemo(
    () => suppliers.filter((supplier) => supplier.status !== "Inactivo").length,
    [suppliers],
  );

  const openCreate = () => {
    setSelectedSupplier(null);
    setForm(emptySupplier);
    setDocuments([]);
    setInvoices([]);
    setNotes([]);
    setError("");
    setNotice("");
    setIsCreating(true);
    setIsEditing(true);
  };

  const saveSupplier = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const response = selectedSupplier
        ? await api.put(`/workspace/suppliers/${selectedSupplier.id}`, form, { withCredentials: true })
        : await api.post("/workspace/suppliers", form, { withCredentials: true });
      await refresh();
      if (selectedSupplier) {
        setSelectedSupplier(response.data);
        setForm({ ...emptySupplier, ...response.data });
        setIsEditing(false);
        setNotice("Los datos del proveedor quedaron guardados.");
      } else {
        setIsCreating(false);
        await loadSupplierDetails(response.data);
        setNotice("Tu proveedor ya está listo.");
      }
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar al proveedor.");
    }
  };

  const uploadDocument = async (file) => {
    if (!file || !selectedSupplier) return;
    setError("");
    const body = new FormData();
    body.append("file", file);
    body.append("category", docCategory);
    try {
      const response = await api.post(`/suppliers/${selectedSupplier.id}/documents`, body, { withCredentials: true });
      setDocuments((current) => [response.data, ...current]);
      setNotice("Documento guardado en el perfil del proveedor.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir el documento.");
    }
  };

  const saveNote = async (event) => {
    event.preventDefault();
    if (!selectedSupplier || !noteDraft.trim()) return;
    setError("");
    try {
      const response = await api.post(
        `/suppliers/${selectedSupplier.id}/notes`,
        { text: noteDraft, reminder_date: noteReminder || null },
        { withCredentials: true },
      );
      setNotes((current) => [response.data, ...current]);
      setNoteDraft("");
      setNoteReminder("");
      setNotice("Nota guardada.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar la nota.");
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text || "");
    setEditingNoteDate(note.reminder_date ? note.reminder_date.slice(0, 10) : "");
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
    setEditingNoteDate("");
  };

  const updateNote = async (event) => {
    event.preventDefault();
    if (!selectedSupplier || !editingNoteId) return;
    setError("");
    try {
      const response = await api.put(
        `/suppliers/${selectedSupplier.id}/notes/${editingNoteId}`,
        { text: editingNoteText, reminder_date: editingNoteDate || null },
        { withCredentials: true },
      );
      setNotes((current) => current.map((note) => (note.id === editingNoteId ? response.data : note)));
      cancelEditNote();
      setNotice("Nota actualizada.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos actualizar la nota.");
    }
  };

  const deleteNote = async (noteId) => {
    if (!selectedSupplier) return;
    setError("");
    try {
      await api.delete(`/suppliers/${selectedSupplier.id}/notes/${noteId}`, { withCredentials: true });
      setNotes((current) => current.filter((note) => note.id !== noteId));
      if (editingNoteId === noteId) cancelEditNote();
      setNotice("Nota eliminada.");
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos eliminar la nota.");
    }
  };

  const messageUrl = selectedSupplier?.whatsapp || selectedSupplier?.phone
    ? `https://wa.me/${(selectedSupplier.whatsapp || selectedSupplier.phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hola ${selectedSupplier.name}, te escribo desde Mi Contador.`)}`
    : "";
  const emailUrl = selectedSupplier?.email
    ? `mailto:${selectedSupplier.email}?subject=${encodeURIComponent("Hola desde Mi Contador")}&body=${encodeURIComponent(`Hola ${selectedSupplier.name},\n\nTe escribo para dar seguimiento.`)}`
    : "";

  return (
    <AppShell>
      <main className="page clients-page">
        <header className="clients-hero">
          <div>
            <small className="eyebrow">COMPRAS Y ALIANZAS</small>
            <h1>Proveedores, sin<br /><em>enredos.</em></h1>
            <p>Ten sus datos, documentos y facturas a la mano.</p>
          </div>
          <button className="clients-add-button" data-testid="suppliers-add-button" onClick={openCreate}>
            <Plus size={19} />
            Nuevo proveedor
          </button>
        </header>

        <section className="clients-overview" aria-label="Resumen de proveedores">
          <article data-testid="suppliers-total-count">
            <small>PROVEEDORES GUARDADOS</small>
            <strong>{suppliers.length}</strong>
          </article>
          <article data-testid="suppliers-active-count">
            <small>ACTIVOS</small>
            <strong>{activeCount}</strong>
          </article>
          <p>Busca por nombre, RFC, categoría o estado.</p>
        </section>

        <div className="clients-workspace">
          <section className="clients-list-panel">
            <label className="clients-search" htmlFor="suppliers-search-input">
              <Search size={18} />
              <input
                id="suppliers-search-input"
                data-testid="suppliers-search-input"
                placeholder="Buscar proveedor"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            {error && !selectedSupplier && <p className="clients-error" data-testid="suppliers-page-error">{error}</p>}
            <div className="clients-list" data-testid="suppliers-list">
              {suppliers.map((supplier) => (
                <button
                  className={`client-row ${selectedSupplier?.id === supplier.id ? "selected" : ""}`}
                  data-testid={`supplier-row-${supplier.id}`}
                  key={supplier.id}
                  onClick={() => loadSupplierDetails(supplier)}
                >
                  <span className="client-avatar">{supplierInitials(supplier.name)}</span>
                  <span className="client-row-copy">
                    <b>{supplier.name}</b>
                    <small>{supplier.rfc || "RFC pendiente"}</small>
                  </span>
                  <i className={`client-status ${supplier.status === "Inactivo" ? "inactive" : ""}`}>{supplier.status || "Activo"}</i>
                </button>
              ))}
              {!suppliers.length && (
                <div className="clients-empty" data-testid="suppliers-empty-state">
                  <b>Aquí aparecerán tus proveedores.</b>
                  <span>Crea el primero para empezar.</span>
                </div>
              )}
            </div>
          </section>

          <section className="client-detail-panel" data-testid="supplier-detail-panel">
            {(isCreating || selectedSupplier) ? (
              <>
                <div className="client-detail-head">
                  <div>
                    <small className="eyebrow">{isCreating ? "NUEVO PROVEEDOR" : "PERFIL DEL PROVEEDOR"}</small>
                    <h2>{isCreating ? "Vamos a conocerlo" : selectedSupplier.name}</h2>
                  </div>
                  {!isCreating && !isEditing && (
                    <button className="client-icon-button" data-testid="supplier-edit-button" onClick={() => setIsEditing(true)} aria-label="Editar proveedor">
                      <Pencil size={18} />
                    </button>
                  )}
                </div>

                {notice && <p className="clients-notice" data-testid="supplier-success-notice">{notice}</p>}
                {error && <p className="clients-error" data-testid="supplier-detail-error">{error}</p>}

                {isEditing ? (
                  <form className="client-form" onSubmit={saveSupplier} data-testid="supplier-form">
                    <label htmlFor="supplier-name">Nombre o razón social
                      <input id="supplier-name" data-testid="supplier-name-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
                    </label>
                    <div className="client-form-grid">
                      <label htmlFor="supplier-type">Tipo de proveedor
                        <select id="supplier-type" data-testid="supplier-type-select" value={form.supplier_type} onChange={(event) => setForm({ ...form, supplier_type: event.target.value })}>
                          <option>Persona física</option>
                          <option>Persona moral</option>
                        </select>
                      </label>
                      <label htmlFor="supplier-status">Estado
                        <select id="supplier-status" data-testid="supplier-status-select" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                          <option>Activo</option>
                          <option>Prospecto</option>
                          <option>Inactivo</option>
                        </select>
                      </label>
                    </div>
                    <div className="client-form-grid">
                      <label htmlFor="supplier-rfc">RFC
                        <input id="supplier-rfc" data-testid="supplier-rfc-input" value={form.rfc} onChange={(event) => setForm({ ...form, rfc: event.target.value.toUpperCase() })} />
                      </label>
                      <label htmlFor="supplier-regime">Régimen fiscal
                        <input id="supplier-regime" data-testid="supplier-regime-input" value={form.tax_regime} onChange={(event) => setForm({ ...form, tax_regime: event.target.value })} />
                      </label>
                    </div>
                    <label htmlFor="supplier-category">Categoría
                      <input id="supplier-category" data-testid="supplier-category-input" placeholder="Renta, papelería, tecnología..." value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} />
                    </label>
                    <label htmlFor="supplier-email">Correo
                      <input id="supplier-email" data-testid="supplier-email-input" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                    </label>
                    <div className="client-form-grid">
                      <label htmlFor="supplier-phone">Teléfono
                        <input id="supplier-phone" data-testid="supplier-phone-input" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
                      </label>
                      <label htmlFor="supplier-whatsapp">WhatsApp
                        <input id="supplier-whatsapp" data-testid="supplier-whatsapp-input" value={form.whatsapp} onChange={(event) => setForm({ ...form, whatsapp: event.target.value })} />
                      </label>
                    </div>
                    <label htmlFor="supplier-address">Domicilio
                      <input id="supplier-address" data-testid="supplier-address-input" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
                    </label>
                    <div className="client-form-actions">
                      {!isCreating && <button className="client-cancel-button" data-testid="supplier-cancel-edit-button" type="button" onClick={() => { setForm({ ...emptySupplier, ...selectedSupplier }); setIsEditing(false); }}>Cancelar</button>}
                      <button className="clients-save-button" data-testid="supplier-save-button">Guardar proveedor</button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="client-quick-actions">
                      <a className={!messageUrl ? "disabled" : ""} data-testid="supplier-whatsapp-link" href={messageUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!messageUrl} onClick={(event) => !messageUrl && event.preventDefault()}>
                        <MessageCircle size={18} />
                        WhatsApp
                      </a>
                      <a className={!emailUrl ? "disabled" : ""} data-testid="supplier-email-link" href={emailUrl || undefined} aria-disabled={!emailUrl} onClick={(event) => !emailUrl && event.preventDefault()}>
                        <Mail size={18} />
                        Correo
                      </a>
                    </div>
                    <dl className="client-fields" data-testid="supplier-profile-info">
                      <div><dt>RFC</dt><dd>{selectedSupplier.rfc || "Pendiente"}</dd></div>
                      <div><dt>Régimen</dt><dd>{selectedSupplier.tax_regime || "Pendiente"}</dd></div>
                      <div><dt>Categoría</dt><dd>{selectedSupplier.category || "Sin categoría"}</dd></div>
                      <div><dt>Correo</dt><dd>{selectedSupplier.email || "Pendiente"}</dd></div>
                      <div><dt>Tipo</dt><dd>{selectedSupplier.supplier_type || "Persona moral"}</dd></div>
                    </dl>
                    <div className="client-section-head">
                      <h3>Documentos</h3>
                      <select data-testid="supplier-document-category-select" value={docCategory} onChange={(event) => setDocCategory(event.target.value)} style={{ marginLeft: "auto", padding: "6px 10px", borderRadius: 10, border: "1px solid var(--line)", background: "#fff", fontSize: 13 }}>
                        <option>Factura</option>
                        <option>Contrato</option>
                        <option>Recibo</option>
                        <option>Estado de cuenta</option>
                        <option>Constancia fiscal</option>
                        <option>Otro</option>
                      </select>
                      <button className="client-upload-button" data-testid="supplier-upload-document-button" onClick={() => uploadInput.current?.click()}>
                        <FileUp size={16} />
                        Subir
                      </button>
                      <input ref={uploadInput} data-testid="supplier-document-input" className="client-hidden-input" type="file" accept="application/pdf,image/*" onChange={(event) => uploadDocument(event.target.files?.[0])} />
                    </div>
                    <p className="client-fields" style={{ padding: "0 0 8px", fontSize: 12, opacity: 0.7 }}>e.firma y CSD estarán disponibles cuando activemos el cifrado seguro.</p>
                    <div className="client-document-list" data-testid="supplier-document-list">
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 0 10px" }}>
                        <small className="eyebrow" style={{ letterSpacing: ".1em" }}>FILTRAR POR CATEGORÍA</small>
                        <select data-testid="supplier-document-filter-select" value={docFilter} onChange={(event) => setDocFilter(event.target.value)} style={{ padding: "6px 10px", borderRadius: 10, border: "1px solid var(--line)", background: "#fff", fontSize: 13 }}>
                          <option>Todas</option>
                          <option>Factura</option>
                          <option>Contrato</option>
                          <option>Recibo</option>
                          <option>Estado de cuenta</option>
                          <option>Constancia fiscal</option>
                          <option>Otro</option>
                        </select>
                      </div>
                      {documents.filter((document) => docFilter === "Todas" || document.category === docFilter).map((document) => <div key={document.id}><FileUp size={15} /><span><b>{document.name}</b>{document.category && <small>{document.category}</small>}</span></div>)}
                      {!documents.filter((document) => docFilter === "Todas" || document.category === docFilter).length && <p>{documents.length ? `Sin documentos en la categoría "${docFilter}".` : "Sin documentos todavía."}</p>}
                    </div>
                    <div className="client-section-head"><h3>Notas y recordatorios</h3><span data-testid="supplier-note-count">{notes.length}</span></div>
                    <form className="client-form" onSubmit={saveNote} data-testid="supplier-note-form" style={{ padding: 0 }}>
                      <label htmlFor="supplier-note-text">Nueva nota
                        <input id="supplier-note-text" data-testid="supplier-note-input" placeholder="Ej: Renovar contrato en mayo" value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} />
                      </label>
                      <div className="client-form-grid">
                        <label htmlFor="supplier-note-date">Recordar el
                          <DatePickerField value={noteReminder} onChange={setNoteReminder} testId="supplier-note-date-input" allowClear />
                        </label>
                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                          <button className="clients-save-button" data-testid="supplier-note-save-button" type="submit">Guardar nota</button>
                        </div>
                      </div>
                    </form>
                    <div className="client-document-list" data-testid="supplier-note-list">
                      {notes.map((note) => (
                        <div key={note.id} data-testid={`supplier-note-item-${note.id}`}>
                          {editingNoteId === note.id ? (
                            <form onSubmit={updateNote} style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, width: "100%" }} data-testid="supplier-note-edit-form">
                              <input data-testid="supplier-note-edit-text" value={editingNoteText} onChange={(event) => setEditingNoteText(event.target.value)} placeholder="Nota" style={{ padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14 }} />
                              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <DatePickerField value={editingNoteDate} onChange={setEditingNoteDate} testId="supplier-note-edit-date" allowClear />
                                <button type="submit" data-testid="supplier-note-edit-save" className="clients-save-button" style={{ padding: "6px 14px", fontSize: 13 }}>Guardar</button>
                                <button type="button" data-testid="supplier-note-edit-cancel" className="client-cancel-button" onClick={cancelEditNote} style={{ padding: "6px 14px", fontSize: 13 }}>Cancelar</button>
                              </div>
                            </form>
                          ) : (
                            <>
                              <span style={{ flex: 1 }}>
                                <b>{note.text}</b>
                                {note.reminder_date && <small>Recordar: {note.reminder_date}</small>}
                              </span>
                              <button type="button" data-testid={`supplier-note-edit-${note.id}`} onClick={() => startEditNote(note)} aria-label="Editar nota" style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "#4169e1" }}>
                                <Pencil size={14} />
                              </button>
                              <button type="button" data-testid={`supplier-note-delete-${note.id}`} onClick={() => deleteNote(note.id)} aria-label="Eliminar nota" style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "#d7565d" }}>
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                      {!notes.length && <p>Sin notas todavía.</p>}
                    </div>
                    <div className="client-section-head"><h3>Facturas y egresos</h3><span data-testid="supplier-invoice-count">{invoices.length}</span></div>
                    <div className="client-invoice-list" data-testid="supplier-invoice-list">
                      {invoices.slice(0, 6).map((invoice) => <div key={invoice.id}><span><b>{invoice.folio || "Egreso"}</b><small>{invoice.status || "Registrado"}</small></span><strong>{money.format(invoice.total || 0)}</strong></div>)}
                      {!invoices.length && <p>Las facturas y egresos de este proveedor se verán aquí.</p>}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="client-empty-detail" data-testid="supplier-detail-empty-state">
                <span>✦</span>
                <h2>Elige un proveedor</h2>
                <p>O crea uno nuevo para guardar sus datos importantes.</p>
                <button data-testid="supplier-empty-create-button" onClick={openCreate}>Crear proveedor</button>
              </div>
            )}
          </section>
        </div>
      </main>
    </AppShell>
  );
}
